import {
  Alert,
  AlertTitle,
  Button,
  capitalize,
  Link,
  Step,
  StepButton,
  Stepper,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { AutonomyMeasurementsStep } from "./steps/AutonomyMeasurementsStep";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { DefaultHandlers, typedPath, TypedPathWrapper } from "typed-path";
import {
  editingSupported,
  implementArchetypeToViewModel,
  ImplementArchetypeViewModel,
  viewModelSchema,
  viewModelToImplementArchetype,
} from "./ImplementArchetypeViewModel";
import { Form, Formik } from "formik";
import { getImplementsPath } from "portal/utils/routing";
import { Loading } from "portal/components/Loading";
import { PtoStep } from "./steps/PtoStep";
import { ReviewStep } from "./steps/ReviewStep";
import { ScvStep } from "./steps/ScvStep";
import { Trans, useTranslation } from "react-i18next";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { validateThen } from "portal/utils/forms";
import LockIcon from "@mui/icons-material/LockOutlined";
import React, { FC, useState } from "react";

interface ImplementArchetypeFormProps {
  archetypeId?: string;
}

const S = typedPath<ImplementArchetypeViewModel>();

const stepFields: Record<number, TypedPathWrapper<unknown, DefaultHandlers>[]> =
  {
    0: [S.make, S.model, S.series, S.hitchType, S.atim],
    1: [
      S.workingWidthM,
      S.workingStartOffsetM,
      S.workingEndOffsetM,
      S.boundaryViewModel,
      S.toolSpeedMS,
      S.transitionSpeedMS,
      S.runUpDistanceM,
      S.straightenDistanceM,
      S.turningRadiusLimits,
    ],
    2: [S.ptoRequirements],
    3: [S.scvInputs],
    // 4: Review — no editable fields
  };

export const ImplementArchetypeForm: FC<ImplementArchetypeFormProps> = ({
  archetypeId,
}) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState<number>(0);
  const navigate = useNavigate();
  const steps: string[] = [
    t("views.autotractor.equipment.basicInfo"),
    t("views.autotractor.equipment.autonomyMeasurements"),
    t("models.equipment.pto_short"),
    t("models.equipment.scv_short", { count: 1 }),
    t("views.autotractor.equipment.review"),
  ];

  const [createImplementArchetype] = useMutationPopups(
    atkEquipmentApi.endpoints.createImplementArchetype.useMutation(),
    {
      success: capitalize(
        t("utils.actions.createdLong", {
          subject: t("models.equipment.archetype_one"),
        })
      ),
    }
  );
  const isEditing = Boolean(archetypeId);
  const { data: archResp, isLoading } = useQueryPopups(
    atkEquipmentApi.endpoints.getImplementArchetype.useQuery(
      { id: archetypeId ?? "" },
      { skip: !isEditing }
    )
  );
  const archetype = archResp;

  const [updateImplementArchetype] = useMutationPopups(
    atkEquipmentApi.endpoints.updateImplementArchetype.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.archetype_one"),
        })
      ),
    }
  );

  if (isEditing && isLoading) {
    return <Loading />;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={implementArchetypeToViewModel(archetype)}
      validationSchema={viewModelSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        const newArchetype = viewModelToImplementArchetype(values);
        if (isEditing) {
          await updateImplementArchetype(newArchetype);
        } else {
          const res = await createImplementArchetype(newArchetype);
          if (!res.error) {
            navigate(getImplementsPath());
          }
        }
        setSubmitting(false);
      }}
    >
      {(formikProps) => {
        const editingUnsupportedVersion = !editingSupported(formikProps.values);

        const buttonRow = (
          <div className="flex justify-between">
            {activeStep > 0 && (
              <Button
                color="inherit"
                onClick={() => setActiveStep(activeStep - 1)}
              >
                {t("utils.actions.backShort")}
              </Button>
            )}
            <div className="flex ml-auto gap-2">
              {activeStep !== steps.length - 1 && (
                <Button
                  className="ml-auto"
                  variant={isEditing ? "text" : "contained"}
                  disabled={!isEditing && activeStep === steps.length - 1}
                  color={isEditing ? "inherit" : "primary"}
                  onClick={() =>
                    validateThen(
                      formikProps,
                      () => setActiveStep(activeStep + 1),
                      undefined,
                      stepFields[activeStep]
                    )
                  }
                >
                  {t("utils.actions.next")}
                </Button>
              )}
              {(isEditing || activeStep === steps.length - 1) && (
                <Button
                  className="ml-auto"
                  type="submit"
                  variant="contained"
                  disabled={
                    !formikProps.isValid ||
                    !formikProps.dirty ||
                    formikProps.isValidating ||
                    formikProps.isSubmitting
                  }
                >
                  {isEditing
                    ? t("utils.actions.save")
                    : t("utils.actions.submit")}
                </Button>
              )}
            </div>
          </div>
        );

        return (
          <Form className="flex flex-col gap-2">
            <Stepper nonLinear={isEditing} activeStep={activeStep}>
              {steps.map((s, i) => (
                <Step key={`${i}-${s}`} index={i}>
                  <StepButton
                    onClick={() =>
                      i > activeStep
                        ? validateThen(
                            formikProps,
                            () => setActiveStep(i),
                            undefined,
                            stepFields[activeStep]
                          )
                        : setActiveStep(i)
                    }
                  >
                    {s}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
            {editingUnsupportedVersion && (
              <Alert icon={<LockIcon fontSize="inherit" />} severity="warning">
                <AlertTitle>
                  {t(
                    "views.autotractor.equipment.unsupportedVersion.implementArchetypeLockedTitle"
                  )}
                </AlertTitle>
                <Trans
                  i18nKey="views.autotractor.equipment.unsupportedVersion.implementArchetypeLockedBody"
                  components={[
                    <Link
                      key="0"
                      href="https://pono-maka.slack.com/archives/C07N6FW2971"
                      color="inherit"
                    />,
                  ]}
                />
              </Alert>
            )}
            {buttonRow}
            {activeStep === 0 && (
              <BasicInfoStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 1 && (
              <AutonomyMeasurementsStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 2 && (
              <PtoStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 3 && (
              <ScvStep disabled={editingUnsupportedVersion} />
            )}
            {activeStep === 4 && (
              <ReviewStep disabled={editingUnsupportedVersion} />
            )}
            {buttonRow}
          </Form>
        );
      }}
    </Formik>
  );
};
